import { sheet } from './day-close'
import { shownClock } from './round-config'
import { dayLabel, el } from './ui'

// Timer nudges, a test (docs/plans/job-times-and-nudges/PLAN.md Part 2; Liam, 21 Oct 2026, "go"). The iPhone app watches
// the lock screen card's next job and the job under way and buzzes "At 3 Ash Lane? Start the job" or "Left 3 Ash Lane?
// Finish the job" (ios TimerNudges.swift). In TestFlight and Xcode builds only, off until turned on here; nothing in a
// browser. Today's row turns it on and off and opens the test list: each day's jobs, what the phone noticed and did,
// and four buttons for Liam to mark how it went (Right, Late, Missed, Wrong), which are how the test is judged.

interface RoundlyApp { call(method: string, params?: unknown): Promise<unknown> }
function roundlyApp(): RoundlyApp | null {
  if (typeof window === 'undefined') return null
  const app = (window as unknown as { RoundlyIOS?: RoundlyApp }).RoundlyIOS
  return app && typeof app.call === 'function' ? app : null
}
async function call<T>(method: string, params?: unknown): Promise<T | null> {
  const app = roundlyApp()
  if (!app) return null
  try { return (await app.call(method, params)) as T } catch { return null }
}

export interface NudgeStatus { available: boolean; on: boolean; location: 'always' | 'whileUsing' | 'denied' | 'restricted' | 'ask'; alerts: boolean; precise: boolean }
export interface NudgeEvent { at: number; visitId: number | null; kind: string; note: string }
export interface NudgeDay { day: string; events: NudgeEvent[]; marks: Record<string, string>; alerted: string[]; jobs: { id: number; street: string; time: string; mapped: boolean }[] }

const MARKS = [['right', 'Right'], ['late', 'Late'], ['missed', 'Missed'], ['wrong', 'Wrong']] as const

// What the row says under "Timer nudges".
export function statusWords(s: NudgeStatus): { note: string; settings: boolean } {
  if (!s.on) return { note: "Buzzes if you arrive at a job and don't press Start job, or leave with the timer running. This iPhone only.", settings: false }
  if (s.location === 'denied' || s.location === 'restricted') return { note: 'Location is off for Roundly, so it can\'t notice you arriving. Turn it on in Settings.', settings: true }
  if (!s.alerts) return { note: 'Notifications are off for Roundly, so it can\'t buzz. Turn them on in Settings.', settings: true }
  if (s.location !== 'always') return { note: 'On, but only while Roundly is open. In Settings, set Location to Always so it notices you with the phone in your pocket.', settings: true }
  if (!s.precise) return { note: "On, but Precise Location is off for Roundly in Settings, so it can't notice you arriving.", settings: true }
  return { note: 'On. Your location stays on this phone.', settings: false }
}

// The row on Today, under Job reminders: null in a browser or an App Store build (filled in once the app answers).
export function nudgesRow(): HTMLElement | null {
  if (!roundlyApp()) return null
  const row = el('section', { class: 'reminders-row nudges-row', 'aria-label': 'Timer nudges, a test' })
  row.hidden = true
  const words = el('div', { class: 'reminders-words' }, el('strong', {}, 'Timer nudges ', el('span', { class: 'nudges-pill' }, 'Test')))
  const note = el('span', { class: 'reminders-note' })
  words.append(note)
  const buttons = el('div', { class: 'nudges-buttons' })
  const toggle = el('button', { type: 'button', class: 'btn-secondary reminders-button' })
  const settings = el('button', { type: 'button', class: 'btn-secondary reminders-button' }, 'Settings')
  const list = el('button', { type: 'button', class: 'btn-secondary reminders-button' }, 'Test list')
  buttons.append(toggle, settings, list)
  row.append(words, buttons)
  const show = (s: NudgeStatus | null): void => {
    if (!s?.available) { row.hidden = true; return }
    row.hidden = false
    const w = statusWords(s)
    note.textContent = w.note
    toggle.textContent = s.on ? 'Turn off' : 'Turn on'
    settings.hidden = !w.settings
  }
  toggle.addEventListener('click', async () => {
    toggle.disabled = true
    const on = toggle.textContent === 'Turn off'
    show(await call<NudgeStatus>(on ? 'nudges.disable' : 'nudges.enable'))
    toggle.disabled = false
  })
  settings.addEventListener('click', () => { void call('settings.open') })
  list.addEventListener('click', () => { void openNudgeList() })
  void call<{ nudges?: boolean }>('app.info').then(async (info) => { if (info?.nudges) show(await call<NudgeStatus>('nudges.status')) })
  return row
}

const clock = (ms: number): string => shownClock(new Date(ms))

// One job's lines, oldest first, its marks, and when Liam really arrived and left (the test's own reference: PLAN.md v3,
// GPT-6.1's review).
export function jobBlock(day: NudgeDay, job: NudgeDay['jobs'][number], mark: (visitId: number, m: string) => Promise<boolean>,
  time: (visitId: number, field: 'arrived' | 'left', value: string) => Promise<boolean> = async () => false): HTMLElement {
  const box = el('div', { class: 'nudges-job' }, el('h3', {}, [job.time, job.street].filter(Boolean).join(' · ')))
  const events = day.events.filter((e) => e.visitId === job.id)
  if (!job.mapped) box.append(el('p', { class: 'nudges-muted' }, 'Not watched: no map position for this customer.'))
  else if (!events.length) box.append(el('p', { class: 'nudges-muted' }, 'Nothing noticed or done for this job.'))
  for (const e of events) box.append(el('p', { class: `nudges-line nudges-${e.kind}` }, el('span', { class: 'nudges-at' }, clock(e.at)), ` ${e.note}`))
  if (job.mapped) {
    const marks = el('div', { class: 'nudges-marks', role: 'group', 'aria-label': `How it went at ${job.street}` })
    for (const [key, words] of MARKS) {
      const b = el('button', { type: 'button', class: `nudges-mark${day.marks[String(job.id)] === key ? ' nudges-mark-on' : ''}`, 'aria-pressed': String(day.marks[String(job.id)] === key) }, words)
      b.addEventListener('click', async () => {
        const next = day.marks[String(job.id)] === key ? '' : key
        if (!(await mark(job.id, next))) return
        if (next) day.marks[String(job.id)] = next
        else delete day.marks[String(job.id)]
        for (const other of marks.querySelectorAll<HTMLButtonElement>('.nudges-mark')) {
          const on = other === b && !!next
          other.classList.toggle('nudges-mark-on', on)
          other.setAttribute('aria-pressed', String(on))
        }
      })
      marks.append(b)
    }
    box.append(marks)
    const times = el('div', { class: 'nudges-times' })
    for (const [field, words] of [['arrived', 'You really arrived'], ['left', 'You really left']] as const) {
      const input = el('input', { type: 'time', value: day.marks[`${job.id}:${field}`] ?? '', 'aria-label': `${words} at ${job.street}` }) as HTMLInputElement
      input.addEventListener('change', () => { void time(job.id, field, input.value) })
      times.append(el('label', { class: 'nudges-time' }, el('span', {}, words), input))
    }
    box.append(times)
  }
  return box
}

export async function openNudgeList(): Promise<void> {
  const got = await call<{ days?: NudgeDay[] }>('nudges.list')
  const s = sheet('Timer nudges test')
  s.head.append(el('h2', {}, 'Timer nudges test'))
  const days = got?.days ?? []
  if (!days.length) {
    s.body.append(el('p', { class: 'nudges-muted' }, 'Nothing yet. Turn it on, work as normal, and each job shows here with what the phone noticed.'))
    return
  }
  s.body.append(el('p', { class: 'nudges-muted' }, 'After the round, mark each job: Right, Late (it buzzed but too late), Missed (it should have buzzed and didn\'t) or Wrong (it buzzed when it shouldn\'t). If you remember roughly when you really arrived and left, put those in too.'))
  for (const day of days) {
    s.body.append(el('h3', { class: 'nudges-day' }, dayLabel(day.day)))
    const general = day.events.filter((e) => e.visitId === null)
    for (const e of general) s.body.append(el('p', { class: 'nudges-line nudges-general' }, el('span', { class: 'nudges-at' }, clock(e.at)), ` ${e.note}`))
    for (const job of day.jobs) s.body.append(jobBlock(day, job,
      async (visitId, m) => (await call<{ ok?: boolean }>('nudges.mark', { day: day.day, visitId, mark: m }))?.ok === true,
      async (visitId, field, value) => (await call<{ ok?: boolean }>('nudges.mark', { day: day.day, visitId, field, value }))?.ok === true))
  }
}

// ---- the phone's noticed times, for "When did you start cleaning?" (0083) -----------------------------------------------

// When the iPhone noticed this person arrive at or leave a job today, from the test's own day record: the moment Start
// job or Slide to finish can offer as "Started then" / "Finished then" (Astra's late-start design, 22 Oct 2026). Only an
// arrival or leaving the iPhone reported as it happened: never "already inside" when it started watching (that is no
// arrival time), never an arrival followed by leaving again, or a leaving followed by coming back. Only between
// ASK_AFTER and NOTICED_FOR ago: pressed sooner the tap's own time is right, and an hour on it is too old to offer (the
// alert's own limit). Empty in a browser, an App Store build, or with the test off.
const ASK_AFTER_MS = 3 * 60_000
const NOTICED_FOR_MS = 60 * 60_000
let noticed: { day: string; events: NudgeEvent[] } | null = null

export async function readNoticed(day: string): Promise<void> {
  if (!roundlyApp()) return
  const got = await call<{ days?: NudgeDay[] }>('nudges.list')
  noticed = { day, events: (got?.days ?? []).find((d) => d.day === day)?.events ?? [] }
}

// `last`: Change times' "Use 10:12 (your phone)", which offers the last arrival or leaving even with the other way since
// and however recent, still within the hour.
export function noticedAt(visitId: number, kind: 'arrived' | 'left', day: string, now: number = Date.now(), last = false): Date | null {
  if (!noticed || noticed.day !== day) return null
  let at: number | null = null
  for (const e of noticed.events.filter((x) => x.visitId === visitId).sort((a, b) => a.at - b.at)) {
    if (e.kind === kind) at = e.at
    // The other way since: back again, or gone again.
    else if (!last && (kind === 'arrived' ? e.kind === 'left' : e.kind === 'arrived' || e.kind === 'inside')) at = null
  }
  return at !== null && now - at >= (last ? 0 : ASK_AFTER_MS) && now - at <= NOTICED_FOR_MS ? new Date(at) : null
}
